import { MeshPhysicalMaterial } from "three";
import { TypedMatNode } from "../_Base";
import { BaseTextureMapController, BooleanParamOptions, NodePathOptions } from "./_BaseTextureController";
import { NodeParamsConfig, ParamConfig } from "../../utils/params/ParamsConfig";
import { Color } from "three";
import { isBooleanTrue } from "../../../../core/BooleanValue";
export function MeshPhysicalParamConfig(Base) {
  return class Mixin extends Base {
    constructor() {
      super(...arguments);
      this.clearcoat = ParamConfig.FLOAT(0, { separatorBefore: true });
      this.useClearCoatMap = ParamConfig.BOOLEAN(0, BooleanParamOptions(MeshPhysicalController));
      this.clearcoatMap = ParamConfig.NODE_PATH("", NodePathOptions(MeshPhysicalController, "useClearCoatMap"));
      this.useClearCoatNormalMap = ParamConfig.BOOLEAN(0, BooleanParamOptions(MeshPhysicalController));
      this.clearcoatNormalMap = ParamConfig.NODE_PATH("", NodePathOptions(MeshPhysicalController, "useClearCoatNormalMap"));
      this.clearcoatNormalScale = ParamConfig.VECTOR2([1, 1], { visibleIf: { useClearCoatNormalMap: 1 } });
      this.clearcoatRoughness = ParamConfig.FLOAT(0);
      this.useClearCoatRoughnessMap = ParamConfig.BOOLEAN(0, BooleanParamOptions(MeshPhysicalController));
      this.clearcoatRoughnessMap = ParamConfig.NODE_PATH("", NodePathOptions(MeshPhysicalController, "useClearCoatRoughnessMap"));
      this.useSheen = ParamConfig.BOOLEAN(0);
      this.sheen = ParamConfig.FLOAT(0, {
        range: [0, 1],
        rangeLocked: [true, false],
        visibleIf: { useSheen: 1 }
      });
      this.sheenRoughness = ParamConfig.FLOAT(1, {
        range: [0, 1],
        rangeLocked: [true, false],
        visibleIf: { useSheen: 1 }
      });
      this.sheenColor = ParamConfig.COLOR([1, 1, 1], {
        visibleIf: { useSheen: 1 }
      });
      this.transmission = ParamConfig.FLOAT(0, {
        range: [0, 1]
      });
      this.useTransmissionMap = ParamConfig.BOOLEAN(0);
      this.transmissionMap = ParamConfig.NODE_PATH("", { visibleIf: { useTransmissionMap: 1 } });
      this.ior = ParamConfig.FLOAT(1.5, {
        range: [1, 2.3333],
        rangeLocked: [true, true]
      });
      this.thickness = ParamConfig.FLOAT(0.01, {
        range: [0, 10],
        rangeLocked: [true, false]
      });
      this.useThicknessMap = ParamConfig.BOOLEAN(0);
      this.thicknessMap = ParamConfig.NODE_PATH("", { visibleIf: { useThicknessMap: 1 } });
      this.attenuationDistance = ParamConfig.FLOAT(0, {
        range: [0, 10],
        rangeLocked: [true, false]
      });
      this.attenuationColor = ParamConfig.COLOR([1, 1, 1]);
    }
  };
}
class TextureClearCoatMapParamsConfig extends MeshPhysicalParamConfig(NodeParamsConfig) {
}
class TextureClearCoatMapMatNode extends TypedMatNode {
}
const tmpMeshPhysicalForIOR = new MeshPhysicalMaterial();
export class MeshPhysicalController extends BaseTextureMapController {
  constructor(node) {
    super(node);
    this.node = node;
    this._sheenColorClone = new Color();
  }
  initializeNode() {
    this.add_hooks(this.node.p.useClearCoatMap, this.node.p.clearcoatMap);
    this.add_hooks(this.node.p.useClearCoatNormalMap, this.node.p.clearcoatNormalMap);
    this.add_hooks(this.node.p.useClearCoatRoughnessMap, this.node.p.clearcoatRoughnessMap);
    this.add_hooks(this.node.p.useTransmissionMap, this.node.p.transmissionMap);
    this.add_hooks(this.node.p.useThicknessMap, this.node.p.thicknessMap);
  }
  async update() {
    this._update(this.node.material, "clearcoatMap", this.node.p.useClearCoatMap, this.node.p.clearcoatMap);
    this._update(this.node.material, "clearcoatNormalMap", this.node.p.useClearCoatNormalMap, this.node.p.clearcoatNormalMap);
    this._update(this.node.material, "clearcoatRoughnessMap", this.node.p.useClearCoatRoughnessMap, this.node.p.clearcoatRoughnessMap);
    this._update(this.node.material, "transmissionMap", this.node.p.useTransmissionMap, this.node.p.transmissionMap);
    this._update(this.node.material, "thicknessMap", this.node.p.useThicknessMap, this.node.p.thicknessMap);
    const pv = this.node.pv;
    tmpMeshPhysicalForIOR.ior = pv.ior;
    const reflectivity = tmpMeshPhysicalForIOR.reflectivity;
    const mat = this.node.material;
    mat.clearcoat = pv.clearcoat;
    if (mat.clearcoatNormalScale != null) {
      mat.clearcoatNormalScale.copy(pv.clearcoatNormalScale);
    }
    mat.clearcoatRoughness = pv.clearcoatRoughness;
    mat.reflectivity = reflectivity;
    if (isBooleanTrue(pv.useSheen)) {
      this._sheenColorClone.copy(pv.sheenColor);
      mat.sheen = pv.sheen;
      mat.sheenRoughness = pv.sheenRoughness;
      mat.sheenColor = this._sheenColorClone;
    } else {
      mat.sheen = 0;
    }
    mat.transmission = pv.transmission;
    mat.thickness = pv.thickness;
    mat.attenuationDistance = pv.attenuationDistance;
    mat.attenuationColor = pv.attenuationColor;
  }
  static async update(node) {
    node.controllers.physical.update();
  }
}
